import type { Product, InstallmentEntry } from '../components/OpportunityPageTypes';

export type ProductRow = Product & { discountType: 'percent' | 'fixed'; discountValue: number };
export const cents = (value: number) => Math.round((value + Number.EPSILON) * 100);
export function rowTotal(row: ProductRow): number {
  const gross = row.price * row.quantity;
  return Math.max(0, cents(gross - (row.discountType === 'fixed' ? row.discountValue : gross * row.discountValue / 100))) / 100;
}
export function rowValid(row: ProductRow): boolean {
  return !!row.productId && Number.isInteger(row.quantity) && row.quantity > 0 &&
    Number.isFinite(row.price) && row.price >= 0 && Number.isFinite(row.discountValue) &&
    row.discountValue >= 0 && row.discountValue <= (row.discountType === 'fixed' ? row.price * row.quantity : 100);
}
/** Allocate a shared payment schedule without duplicating the batch total on each product. */
export function allocateInstallments(totals: number[], schedule: InstallmentEntry[]): InstallmentEntry[][] {
  const remaining = schedule.map(entry => cents(entry.amount ?? 0));
  const difference = totals.reduce((sum, value) => sum + cents(value), 0) - remaining.reduce((a, b) => a + b, 0);
  // Match the one-cent tolerance accepted by the payment form. Reconcile the
  // rounding remainder before allocating, so a valid Save cannot throw here.
  if (!remaining.length || Math.abs(difference) > 1 || remaining[remaining.length - 1] + difference < 0) {
    throw new Error('Installment amounts must match the product total');
  }
  remaining[remaining.length - 1] += difference;
  return totals.map(total => {
    const rowCents = cents(total);
    const available = remaining.reduce((a, b) => a + b, 0);
    const exact = remaining.map(value => available ? rowCents * value / available : 0);
    const amounts = exact.map(Math.floor);
    let rest = rowCents - amounts.reduce((a, b) => a + b, 0);
    const order = exact.map((value, i) => ({ i, fraction: value - amounts[i] })).sort((a, b) => b.fraction - a.fraction);
    for (const { i } of order) {
      if (rest > 0 && amounts[i] < remaining[i]) { amounts[i]++; rest--; }
    }
    return schedule.map((entry, i) => {
      remaining[i] -= amounts[i];
      return { ...entry, amount: amounts[i] / 100 };
    });
  });
}
export function toProduct(row: ProductRow, payment: Product['payment']): Product {
  const { discountType, discountValue, ...product } = row;
  const gross = row.price * row.quantity;
  return { ...product, discountType, discountValue, discount: discountType === 'fixed' ? (gross ? discountValue / gross * 100 : 0) : discountValue, payment };
}
